import { BidCommitteeMemberTypeService } from "./../services/bidCommitteeMemberType.service";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { ModuleService } from "services/module.service";
import { IModule } from "models/ModuleModel";
import { Shared } from "utility/shared";
import { RootState } from "./store";
import { IDocumentType } from "models/DocumentTypeModel";
import { DocumentTypeService } from "services/documentType.service";
import { IGeneralStatus } from "models/GeneralStatusModel";
import { GeneralStatusService } from "services/generalStatus.service";
import {
	IProcurementMethodType,
	IProcurementMethod,
	IProcurementMethodCategory,
} from "models/ProcurementMethod/procurementMethodModel";
import { IEvaluationMethod } from "models/EvaluationMethod/evaluationMethodModel";
import { ProcurementMethodService } from "services/procurementMethod.service";
import { EvaluationMethodService } from "services/evaluationMethod.service";
import { IItemAvailability } from "models/Item/itemAvailabilityModel";
import { ItemService } from "services/item.service";
import {
	IBidCommitteeMemberRole,
	IBidCommitteeType,
} from "models/BidCommittee/BidCommitteeModel";
import { BidCommitteeService } from "services/bidCommittee.service";
import { ProgrammeService } from "services/programme.service";
import { IProgrammeList } from "models/Programme/ProgrammeListModel";
import { IItemNameModel } from "models/Item/ItemNameModel";
import { CSDCommodityService } from "services/csdCommodity.service";
import {
	ICSDSegment,
	ISupplierChangeStatus,
	ISupplierChangeType,
} from "models/CSDIntegration/CSDIntegrationModel";
import { TemplateTypeService } from "services/templateType.service";
import { ISystemTemplateType } from "models/TemplateType/TemplateTypeModel";
import {
	IBudgetAllocationStatus,
	IBudgetGlCategory,
} from "models/Budget/BudgetAllocationDetail";
import { BudgetAllocationService } from "services/budgetAllocation.service";
import {
	DashboardFilterStateModel,
	dashboardFilterStateInitialValue,
} from "hooks/useDashboardFilter";
import { BidService } from "services/bid.service";
import { IBidConversionType } from "models/Bid/BidDetailModel";
import { IErpDbCompany } from "models/ErpDbCompanyModel";
import { ErpDbCompanyService } from "services/erpDbCompany.service";
import {
	ContractDashboardFilterStateModel,
	contractDashboardFilterStateInitialValue,
} from "hooks/useContractDashboardFilter";
import { IAddressType } from "models/AddressType/addressTypeModel";
import { AddressTypeService } from "services/addressType.service";
import { IPortfolio } from "models/Portfolio/portfolioModel";
import { PortfolioService } from "services/portfolio.service";
import { ICountry } from "models/VendorPortal/companyProfileDetail";
import { CompanyProfileService } from "services/companyProfile.service";
import { SupplierChangeTypeService } from "services/supplierChangeType.service";
import { SupplierChangeStatusService } from "services/supplierDetailChangeStatus.service";
import { IEftModule } from "models/EftModuleModel";
import { EftModuleService } from "services/eftModule.service";
import { ICreditorSourceId } from "models/CreditorIdSourceModel";
import { CreditorIdSourceService } from "services/creditorIdSource.service";
import { IBidCommitteeStatus } from "models/BidCommittee/BidCommitteeStatusModel";
import { BidCommitteeStatusService } from "services/bidCommitteeStatus.service";
import {
	IBidCommitteeMemberStatus,
	IBidCommitteeMemberType,
} from "models/BidCommittee/BidCommitteeMemberStatusModel";
import { BidCommitteeMemberStatusService } from "services/bidCommitteeMemberStatus.service";
import { INotificationFrequency } from "models/NotificationFrequencyModel";
import { INotificationPeriod } from "models/NotificationPeriodModel";
import { INotificationRole } from "models/NotificationRoleModel";
import { NotificationPeriodService } from "services/notificationPeriod.service";
import { NotificationRoleService } from "services/notificationRole.service";
import { NotificationFrequencyService } from "services/notificationFrequency.service";
import { IOpeningProcedure } from "models/OpeningProcedureModel";
import { OpeningProcedureService } from "services/openingProcedure.service";
import { IEvaluationItemType } from "models/Bid/EvaluationItemTypeModel";
import { EvaluationItemTypeService } from "services/evaluationItemType.service";
import { IEvaluationFunctionalityType } from "models/EvaluationFunctionalityTypeModel";
import { EvaluationFunctionalityTypeService } from "services/evaluationFunctionalityType.service";
import { OrganizationEvaluationSettingService } from "services/organizationPriceEvaluationSetting.service";
import { IOrganizationEvaluationNumber } from "models/OrganizationEvaluationSettingModel";
import { ISupportingDocument } from "models/EvaluationSupportingDocumentModel";
import { EvaluationSupportingDocumentService } from "services/evaluationSupportingDocument.service";
import {
	IEquationBracket,
	IEquationFields,
	IEquationOperator,
	IEquationVariable,
} from "models/EquationModel";
import { EquationService } from "services/equation.service";
import { IMeasurementArea } from "models/MeasurementAreaModel";
import { MeasurementAreaService } from "services/measurementArea.service";
import {
	IPerformanceEvaluationDefinitionScopeOfWorks,
	IScopeOfWork,
} from "models/ScopeOfWorkModel";
import { ScopeOfWorkService } from "services/scopeOfWork.service";
import { IPerformanceEvaluationOperation } from "models/PerformanceEvaluationOperationModel";
import { PerformanceEvaluationOperationService } from "services/performanceEvaluationOperation.service";
import { IPerformanceEvaluationStatus } from "models/PerformanceEvaluationStatusModel";
import { PerformanceEvaluationStatusService } from "services/performanceEvaluationStatus.service";
import { IPerformanceEvaluationMethod } from "models/PerformanceEvaluationMethodModel";
import { IPerformanceEvaluationType } from "models/PerformanceEvaluationTypeModel";
import { PerformanceEvaluationTypeService } from "services/performanceEvaluationType.service";
import { IRatingScale } from "models/RatingScaleModel";
import { RatingScaleService } from "services/ratingScale.service";
import { PerformanceEvaluationMethodService } from "services/performanceEvaluationMethod.service";
import { PerformanceEvaluationDefinitionScopeOfWorkService } from "services/performanceEvaluationDefinitionScopeOfWork.service";

export interface MasterPageState {
	systemTemplateModuleList: IModule[];
	generalStatusList: IGeneralStatus[];
	systemTemplateTypeList: ISystemTemplateType[];
	procurementMethodList: IProcurementMethod[];
	documentTypeList: IDocumentType[];
	procurementMethodTypeList: IProcurementMethodType[];
	evaluationMethodList: IEvaluationMethod[];
	itemAvailabilityList: IItemAvailability[];
	bidCommitteeTypeList: IBidCommitteeType[];
	bidCommitteeMemberRoleList: IBidCommitteeMemberRole[];
	programmeList: IProgrammeList[];
	csdSegmentList: ICSDSegment[];
	itemList: IItemNameModel[];
	budgetAllocationStatusList: IBudgetAllocationStatus[];
	glCategoryList: IBudgetGlCategory[];
	dashboardFilterDetail: DashboardFilterStateModel;
	procurementMethodCategoryList: IProcurementMethodCategory[];
	bidConversionTypeList: IBidConversionType[];
	allErpDbCompanyList: IErpDbCompany[];
	contractDashboardFilterDetail: ContractDashboardFilterStateModel;
	allAddressTypeList: IAddressType[];
	allPortfolioList: IPortfolio[];
	allCountryList: ICountry[];
	allSupplierChangeType: ISupplierChangeType[];
	allSupplierChangeStatus: ISupplierChangeStatus[];
	allEftModule: IEftModule[];
	allCreditorIdSource: ICreditorSourceId[];
	allBidCommitteeStatus: IBidCommitteeStatus[];
	allBidCommitteeMemberStatus: IBidCommitteeMemberStatus[];
	allBidCommitteeMemberType: IBidCommitteeMemberType[];
	allNotificationFrequency: INotificationFrequency[];
	allNotificationPeriod: INotificationPeriod[];
	allNotificationRole: INotificationRole[];
	allOpeningProcedure: IOpeningProcedure[];
	allEvaluationItemType: IEvaluationItemType[];
	allEvaluationFunctionalityType: IEvaluationFunctionalityType[];
	allPriceAndBeeSettingList: IOrganizationEvaluationNumber[];
	allSupportingDocumentList: ISupportingDocument[];
	allEquationBracketList: IEquationBracket[];
	allEquationFieldList: IEquationFields[];
	allEquationOperatorList: IEquationOperator[];
	allEquationVariableList: IEquationVariable[];
	allMeasurementAreaList: IMeasurementArea[];
	allScopeOfWorkList: IScopeOfWork[];
	allPerformanceEvaluationOperationList: IPerformanceEvaluationOperation[];
	allPerformanceEvaluationStatusList: IPerformanceEvaluationStatus[];
	allPerformanceEvaluationMethodList: IPerformanceEvaluationMethod[];
	allPerformanceEvaluationTypeList: IPerformanceEvaluationType[];
	allRatingScaleList: IRatingScale[];
	allApprovedScopeOfWorkList: IPerformanceEvaluationDefinitionScopeOfWorks[];
}

const initialState: MasterPageState = {
	systemTemplateModuleList: [],
	generalStatusList: [],
	systemTemplateTypeList: [],
	procurementMethodList: [],
	documentTypeList: [],
	procurementMethodTypeList: [],
	evaluationMethodList: [],
	itemAvailabilityList: [],
	bidCommitteeTypeList: [],
	bidCommitteeMemberRoleList: [],
	programmeList: [],
	csdSegmentList: [],
	itemList: [],
	budgetAllocationStatusList: [],
	glCategoryList: [],
	dashboardFilterDetail: dashboardFilterStateInitialValue,
	procurementMethodCategoryList: [],
	bidConversionTypeList: [],
	allErpDbCompanyList: [],
	contractDashboardFilterDetail: contractDashboardFilterStateInitialValue,
	allAddressTypeList: [],
	allPortfolioList: [],
	allCountryList: [],
	allSupplierChangeType: [],
	allSupplierChangeStatus: [],
	allEftModule: [],
	allCreditorIdSource: [],
	allBidCommitteeStatus: [],
	allBidCommitteeMemberStatus: [],
	allBidCommitteeMemberType: [],
	allNotificationFrequency: [],
	allNotificationPeriod: [],
	allNotificationRole: [],
	allOpeningProcedure: [],
	allEvaluationItemType: [],
	allEvaluationFunctionalityType: [],
	allPriceAndBeeSettingList: [],
	allSupportingDocumentList: [],
	allEquationBracketList: [],
	allEquationFieldList: [],
	allEquationOperatorList: [],
	allEquationVariableList: [],
	allMeasurementAreaList: [],
	allScopeOfWorkList: [],
	allPerformanceEvaluationOperationList: [],
	allPerformanceEvaluationStatusList: [],
	allPerformanceEvaluationMethodList: [],
	allPerformanceEvaluationTypeList: [],
	allRatingScaleList: [],
	allApprovedScopeOfWorkList: [],
};

export const fetchSystemTemplateModuleList = createAsyncThunk(
	"getSystemTemplateModuleList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.systemTemplateModuleList.length === 0) {
			return await ModuleService.getSystemTemplateModuleList();
		}
	}
);

export const fetchGeneralStatusList = createAsyncThunk(
	"getSystemTemplateStatusList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.generalStatusList.length === 0) {
			return await GeneralStatusService.getGeneralStatusList();
		}
	}
);

export const fetchSystemTemplateTypeList = createAsyncThunk(
	"getSystemTemplateTypeList",
	async (isForceAPICall: boolean, { getState }) => {
		const state = getState() as RootState;
		if (
			state.masterPageState.systemTemplateTypeList.length === 0 ||
			isForceAPICall
		) {
			return await TemplateTypeService.getSystemTemplateTypeList();
		}
	}
);

export const fetchProcurementMethodList = createAsyncThunk(
	"getProcurementMethodList",
	async (isForceAPICall: boolean, { getState }) => {
		const state = getState() as RootState;
		if (
			state.masterPageState.procurementMethodList.length === 0 ||
			isForceAPICall
		) {
			return await ProcurementMethodService.getProcurementMethodList();
		}
	}
);

export const fetchDocumentTypeList = createAsyncThunk(
	"getDocumentTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.documentTypeList.length === 0) {
			return await DocumentTypeService.getDocumentTypeList();
		}
	}
);

export const fetchProcurementMethodTypeList = createAsyncThunk(
	"getProcurementMethodTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.procurementMethodTypeList.length === 0) {
			return await ProcurementMethodService.getProcurementMethodTypeList([]);
		}
	}
);

export const fetchAllEvaluationMethodList = createAsyncThunk(
	"getAllEvaluationMethodList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.evaluationMethodList.length === 0) {
			return await EvaluationMethodService.getAllEvaluationMethodList();
		}
	}
);

export const fetchItemAvailabilityList = createAsyncThunk(
	"getItemAvailabilityList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.itemAvailabilityList.length === 0) {
			return await ItemService.getItemAvailabilityList();
		}
	}
);

export const fetchBidCommitteeTypeList = createAsyncThunk(
	"getBidCommitteeTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.bidCommitteeTypeList.length === 0) {
			return await BidCommitteeService.getBidCommitteeTypeList();
		}
	}
);

export const fetchBidCommitteeMemberRoleList = createAsyncThunk(
	"getBidCommitteeMemberRoleList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.bidCommitteeMemberRoleList.length === 0) {
			return await BidCommitteeService.getBidCommitteeMemberRoleList();
		}
	}
);

export const fetchProgrammeList = createAsyncThunk(
	"getAllProgrammeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.programmeList.length === 0) {
			return await ProgrammeService.getAllProgrammeList();
		}
	}
);

export const fetchCsdSegmentList = createAsyncThunk(
	"getCsdSegmentList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.csdSegmentList.length === 0) {
			return await CSDCommodityService.getCsdSegmentList();
		}
	}
);

export const fetchBudgetAllocationStatusList = createAsyncThunk(
	"getBudgetAllocationStatusList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.budgetAllocationStatusList.length === 0) {
			return await BudgetAllocationService.getBudgetAllocationStatusList();
		}
	}
);

export const fetchGlCategoryList = createAsyncThunk(
	"getGlCategoryList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.glCategoryList.length === 0) {
			return await BudgetAllocationService.getGlCategoryList();
		}
	}
);

export const fetchProcurementMethodCategoryList = createAsyncThunk(
	"getProcurementMethodCategoryList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.procurementMethodCategoryList.length === 0) {
			return await ProcurementMethodService.getProcurementMethodCategoryList();
		}
	}
);

export const fetchBidConversionTypeList = createAsyncThunk(
	"getBidConversionTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.bidConversionTypeList.length === 0) {
			return await BidService.getBidConversionTypeList();
		}
	}
);

export const fetchAllErpDbCompanyList = createAsyncThunk(
	"getErpDbCompanyList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allErpDbCompanyList.length === 0) {
			return await ErpDbCompanyService.getErpDbCompanyList();
		}
	}
);

export const fetchAllAddressTypeList = createAsyncThunk(
	"getAddressTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allAddressTypeList.length === 0) {
			return await AddressTypeService.getAddressTypeList();
		}
	}
);

export const fetchAllPortfolioList = createAsyncThunk(
	"getPortfolioList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allPortfolioList.length === 0) {
			return await PortfolioService.getPortfolioList();
		}
	}
);

export const fetchAllCountryList = createAsyncThunk(
	"getCountryList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allCountryList.length === 0) {
			return await CompanyProfileService.getCountryList();
		}
	}
);

export const fetchAllSupplierChangeType = createAsyncThunk(
	"getSupplierChangeTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allSupplierChangeType.length === 0) {
			return await SupplierChangeTypeService.getSupplierChangeTypeList();
		}
	}
);

export const fetchAllSupplierChangeStatus = createAsyncThunk(
	"getSupplierChangeStatusList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allSupplierChangeStatus.length === 0) {
			return await SupplierChangeStatusService.getSupplierChangeStatusList();
		}
	}
);

export const fetchAllEftModule = createAsyncThunk(
	"getEftModuleList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allEftModule.length === 0) {
			return await EftModuleService.getEftModuleList();
		}
	}
);

export const fetchAllCreditorIdSource = createAsyncThunk(
	"getCreditorIdSourceList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allEftModule.length === 0) {
			return await CreditorIdSourceService.getCreditorIdSourceList();
		}
	}
);

export const fetchAllBidCommitteeStatus = createAsyncThunk(
	"getBidCommitteeStatus",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allBidCommitteeStatus.length === 0) {
			return await BidCommitteeStatusService.getBidCommitteeStatusList();
		}
	}
);

export const fetchAllBidCommitteeMemberStatus = createAsyncThunk(
	"getBidCommitteeMemberStatus",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allBidCommitteeMemberStatus.length === 0) {
			return await BidCommitteeMemberStatusService.getBidCommitteeMemberStatusList();
		}
	}
);

export const fetchAllBidCommitteeMemberType = createAsyncThunk(
	"getBidCommitteeMemberType",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allBidCommitteeMemberType.length === 0) {
			return await BidCommitteeMemberTypeService.getBidCommitteeMemberTypeList();
		}
	}
);

export const fetchAllNotificationPeriod = createAsyncThunk(
	"getNotificationPeriod",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allNotificationPeriod.length === 0) {
			return await NotificationPeriodService.getNotificationPeriodList();
		}
	}
);

export const fetchAllNotificationRole = createAsyncThunk(
	"getNotificationRole",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allNotificationRole.length === 0) {
			return await NotificationRoleService.getNotificationRoleList();
		}
	}
);

export const fetchAllNotificationFrequency = createAsyncThunk(
	"getNotificationFrequency",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allNotificationFrequency.length === 0) {
			return await NotificationFrequencyService.getNotificationFrequencyList();
		}
	}
);

export const fetchAllOpeningProcedure = createAsyncThunk(
	"getOpeningProcedureList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allOpeningProcedure.length === 0) {
			return await OpeningProcedureService.getOpeningProcedureList();
		}
	}
);

export const fetchAllEvaluationItemType = createAsyncThunk(
	"getEvaluationItemTypeList",
	async (evaluationPhaseId: number = 0, { getState }) => {
		const state = getState() as RootState;
		if (
			state.masterPageState.allEvaluationItemType.length === 0 ||
			evaluationPhaseId > 0
		) {
			return await EvaluationItemTypeService.getEvaluationItemTypeList(
				evaluationPhaseId
			);
		}
	}
);

export const fetchAllEvaluationFunctionalityType = createAsyncThunk(
	"getEvaluationFunctionalityTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allEvaluationFunctionalityType.length === 0) {
			return await EvaluationFunctionalityTypeService.getEvaluationFunctionalityTypeList();
		}
	}
);

export const fetchAllPriceAndBeeEvaluationSettingList = createAsyncThunk(
	"getAllNumberList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allPriceAndBeeSettingList.length === 0) {
			return await OrganizationEvaluationSettingService.getAllNumberList();
		}
	}
);

export const fetchAllEvaluationSupportingDocumentList = createAsyncThunk(
	"getAllEvaluationSupportingDocumentList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allEvaluationFunctionalityType.length === 0) {
			return await EvaluationSupportingDocumentService.getAllEvaluationSupportingDocumentList();
		}
	}
);

export const fetchAllEquationBracketList = createAsyncThunk(
	"getAllEquationBracketList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allEquationBracketList.length === 0) {
			return await EquationService.getEquationBracketList();
		}
	}
);

export const fetchAllEquationFieldList = createAsyncThunk(
	"getAllEquationFieldList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allEquationFieldList.length === 0) {
			return await EquationService.getEquationFieldList();
		}
	}
);

export const fetchAllEquationOperatorList = createAsyncThunk(
	"getAllEquationOperatorList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allEquationOperatorList.length === 0) {
			return await EquationService.getEquationOperationList();
		}
	}
);

export const fetchAllEquationVariableList = createAsyncThunk(
	"getAllEquationVariableList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allEquationVariableList.length === 0) {
			return await EquationService.getEquationVariableList();
		}
	}
);

export const fetchAllMeasurementAreaList = createAsyncThunk(
	"getAllMeasurementAreaList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allMeasurementAreaList.length === 0) {
			return await MeasurementAreaService.getMeasurementAreaList();
		}
	}
);

export const fetchAllScopeOfWorkList = createAsyncThunk(
	"getAllScopeOfWorkList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allScopeOfWorkList.length === 0) {
			return await ScopeOfWorkService.getScopeOfWorkList();
		}
	}
);

export const fetchAllPerformanceEvaluationOperationList = createAsyncThunk(
	"getAllPerformanceEvaluationOperationList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (
			state.masterPageState.allPerformanceEvaluationOperationList.length === 0
		) {
			return await PerformanceEvaluationOperationService.getPerformanceEvaluationOperationList();
		}
	}
);

export const fetchAllPerformanceEvaluationStatusList = createAsyncThunk(
	"getAllPerformanceEvaluationStatusList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allPerformanceEvaluationStatusList.length === 0) {
			return await PerformanceEvaluationStatusService.getPerformanceEvaluationStatusList();
		}
	}
);

export const fetchAllPerformanceEvaluationMethodList = createAsyncThunk(
	"getAllPerformanceEvaluationMethodList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allPerformanceEvaluationMethodList.length === 0) {
			return await PerformanceEvaluationMethodService.getPerformanceEvaluationMethodList();
		}
	}
);

export const fetchAllPerformanceEvaluationTypeList = createAsyncThunk(
	"getAllPerformanceEvaluationTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allPerformanceEvaluationTypeList.length === 0) {
			return await PerformanceEvaluationTypeService.getPerformanceEvaluationTypeList();
		}
	}
);

export const fetchAllRatingScaleList = createAsyncThunk(
	"getAllRatingScaleList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allRatingScaleList.length === 0) {
			return await RatingScaleService.getRatingScaleList();
		}
	}
);

export const fetchAllApprovedActiveScopeOfWorkList = createAsyncThunk(
	"getAllApprovedActiveScopeOfWorkList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.masterPageState.allApprovedScopeOfWorkList.length === 0) {
			return await PerformanceEvaluationDefinitionScopeOfWorkService.getApprovedActiveScopeOfWorksList();
		}
	}
);

export const masterPageStateSlice = createSlice({
	name: "masterPageState",
	initialState,
	reducers: {
		setSelectedDashboardFilter: (state, action) => {
			state.dashboardFilterDetail = action.payload;
		},
		setSelectedContractDashboardFilter: (state, action) => {
			state.contractDashboardFilterDetail = action.payload;
		},
	},
	extraReducers: {
		[fetchSystemTemplateModuleList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.systemTemplateModuleList = action.payload;
			}
		},
		[fetchSystemTemplateModuleList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading system template module List, please try again!"
			);
		},
		[fetchGeneralStatusList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.generalStatusList = action.payload;
			}
		},
		[fetchGeneralStatusList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading system template status List, please try again!"
			);
		},
		[fetchSystemTemplateTypeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.systemTemplateTypeList = action.payload;
			}
		},
		[fetchSystemTemplateTypeList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading system template document type List, please try again!"
			);
		},
		[fetchProcurementMethodList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.procurementMethodList = action.payload;
			}
		},
		[fetchProcurementMethodList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading procurement method List, please try again!"
			);
		},
		[fetchDocumentTypeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.documentTypeList = action.payload;
			}
		},
		[fetchDocumentTypeList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading document type List, please try again!"
			);
		},
		[fetchProcurementMethodTypeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.procurementMethodTypeList = action.payload;
			}
		},
		[fetchProcurementMethodTypeList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading procurement method type list, please try again!"
			);
		},
		[fetchAllEvaluationMethodList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.evaluationMethodList = action.payload;
			}
		},
		[fetchAllEvaluationMethodList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading evaluation method list, please try again!"
			);
		},
		[fetchItemAvailabilityList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.itemAvailabilityList = action.payload;
			}
		},
		[fetchItemAvailabilityList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading item availability list, please try again!"
			);
		},
		[fetchBidCommitteeTypeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.bidCommitteeTypeList = action.payload;
			}
		},
		[fetchBidCommitteeTypeList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading bid committee type list, please try again!"
			);
		},
		[fetchBidCommitteeMemberRoleList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.bidCommitteeMemberRoleList = action.payload;
			}
		},
		[fetchBidCommitteeMemberRoleList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading bid committee member role list, please try again!"
			);
		},
		[fetchProgrammeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.programmeList = action.payload;
			}
		},
		[fetchProgrammeList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading programme list, please try again!"
			);
		},
		[fetchCsdSegmentList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.csdSegmentList = action.payload;
			}
		},
		[fetchCsdSegmentList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading csd segment List, please try again!"
			);
		},
		[fetchBudgetAllocationStatusList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.budgetAllocationStatusList = action.payload;
			}
		},
		[fetchBudgetAllocationStatusList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading budget allocation status list, please try again!"
			);
		},
		[fetchGlCategoryList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.glCategoryList = action.payload;
			}
		},
		[fetchGlCategoryList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching gl category list, please try again!"
			);
		},
		[fetchProcurementMethodCategoryList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.procurementMethodCategoryList = action.payload;
			}
		},
		[fetchProcurementMethodCategoryList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching procurement method category list, please try again!"
			);
		},
		[fetchBidConversionTypeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.bidConversionTypeList = action.payload;
			}
		},
		[fetchBidConversionTypeList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching bid conversion type list, please try again!"
			);
		},
		[fetchAllErpDbCompanyList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allErpDbCompanyList = action.payload;
			}
		},
		[fetchAllErpDbCompanyList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching ERP DB company list, please try again!"
			);
		},
		[fetchAllAddressTypeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allAddressTypeList = action.payload;
			}
		},
		[fetchAllAddressTypeList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching Address Type list, please try again!"
			);
		},
		[fetchAllPortfolioList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allPortfolioList = action.payload;
			}
		},
		[fetchAllPortfolioList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching Portfolio list, please try again!"
			);
		},
		[fetchAllCountryList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allCountryList = action.payload;
			}
		},
		[fetchAllCountryList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching country list, please try again!"
			);
		},
		[fetchAllSupplierChangeType.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allSupplierChangeType = action.payload;
			}
		},
		[fetchAllSupplierChangeType.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching supplier change type list, please try again!"
			);
		},
		[fetchAllSupplierChangeStatus.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allSupplierChangeStatus = action.payload;
			}
		},
		[fetchAllSupplierChangeStatus.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching supplier change status list, please try again!"
			);
		},
		[fetchAllEftModule.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allEftModule = action.payload;
			}
		},
		[fetchAllEftModule.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching eft module list, please try again!"
			);
		},
		[fetchAllCreditorIdSource.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allCreditorIdSource = action.payload;
			}
		},
		[fetchAllCreditorIdSource.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching creditor id source list, please try again!"
			);
		},
		[fetchAllBidCommitteeStatus.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allBidCommitteeStatus = action.payload;
			}
		},
		[fetchAllBidCommitteeStatus.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching bid committee status list, please try again!"
			);
		},
		[fetchAllBidCommitteeMemberStatus.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allBidCommitteeMemberStatus = action.payload;
			}
		},
		[fetchAllBidCommitteeMemberStatus.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching bid committee member status list, please try again!"
			);
		},
		[fetchAllBidCommitteeMemberType.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allBidCommitteeMemberType = action.payload;
			}
		},
		[fetchAllBidCommitteeMemberType.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching bid committee member type list, please try again!"
			);
		},
		[fetchAllNotificationFrequency.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allNotificationFrequency = action.payload;
			}
		},
		[fetchAllNotificationFrequency.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching notification frequency list, please try again!"
			);
		},
		[fetchAllNotificationRole.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allNotificationRole = action.payload;
			}
		},
		[fetchAllNotificationRole.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching notification role list, please try again!"
			);
		},
		[fetchAllNotificationPeriod.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allNotificationPeriod = action.payload;
			}
		},
		[fetchAllNotificationPeriod.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching notification Period list, please try again!"
			);
		},
		[fetchAllOpeningProcedure.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allOpeningProcedure = action.payload;
			}
		},
		[fetchAllOpeningProcedure.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching opening procedure list, please try again!"
			);
		},
		[fetchAllEvaluationItemType.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allEvaluationItemType = action.payload;
			}
		},
		[fetchAllEvaluationItemType.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching evaluation item type list, please try again!"
			);
		},
		[fetchAllEvaluationFunctionalityType.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allEvaluationFunctionalityType = action.payload;
			}
		},
		[fetchAllEvaluationFunctionalityType.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching evaluation functionality type list, please try again!"
			);
		},
		[fetchAllPriceAndBeeEvaluationSettingList.fulfilled.type]: (
			state,
			action
		) => {
			if (action.payload && action.payload.length > 0) {
				state.allPriceAndBeeSettingList = action.payload;
			}
		},
		[fetchAllPriceAndBeeEvaluationSettingList.rejected.type]: (
			_state,
			_action
		) => {
			Shared.showSnackbarMessage(
				"Error while fetching price and preference evaluation setting list, please try again!"
			);
		},
		[fetchAllEvaluationSupportingDocumentList.fulfilled.type]: (
			state,
			action
		) => {
			if (action.payload && action.payload.length > 0) {
				state.allSupportingDocumentList = action.payload;
			}
		},
		[fetchAllEvaluationSupportingDocumentList.rejected.type]: (
			_state,
			_action
		) => {
			Shared.showSnackbarMessage(
				"Error while fetching evaluation supporting document list, please try again!"
			);
		},
		[fetchAllEquationBracketList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allEquationBracketList = action.payload;
			}
		},
		[fetchAllEquationBracketList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching equation bracket list, please try again!"
			);
		},
		[fetchAllEquationFieldList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allEquationFieldList = action.payload;
			}
		},
		[fetchAllEquationFieldList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching equation field list, please try again!"
			);
		},
		[fetchAllEquationOperatorList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allEquationOperatorList = action.payload;
			}
		},
		[fetchAllEquationOperatorList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching equation operator list, please try again!"
			);
		},
		[fetchAllEquationVariableList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allEquationVariableList = action.payload;
			}
		},
		[fetchAllEquationVariableList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching equation variable list, please try again!"
			);
		},
		[fetchAllMeasurementAreaList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allMeasurementAreaList = action.payload;
			}
		},
		[fetchAllMeasurementAreaList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching measurement area list, please try again!"
			);
		},
		[fetchAllScopeOfWorkList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allScopeOfWorkList = action.payload;
			}
		},
		[fetchAllScopeOfWorkList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching scope of work list, please try again!"
			);
		},
		[fetchAllPerformanceEvaluationOperationList.fulfilled.type]: (
			state,
			action
		) => {
			if (action.payload && action.payload.length > 0) {
				state.allPerformanceEvaluationOperationList = action.payload;
			}
		},
		[fetchAllPerformanceEvaluationOperationList.rejected.type]: (
			_state,
			_action
		) => {
			Shared.showSnackbarMessage(
				"Error while fetching performance evaluation operation list, please try again!"
			);
		},
		[fetchAllPerformanceEvaluationStatusList.fulfilled.type]: (
			state,
			action
		) => {
			if (action.payload && action.payload.length > 0) {
				state.allPerformanceEvaluationStatusList = action.payload;
			}
		},
		[fetchAllPerformanceEvaluationStatusList.rejected.type]: (
			_state,
			_action
		) => {
			Shared.showSnackbarMessage(
				"Error while fetching performance evaluation status list, please try again!"
			);
		},
		[fetchAllPerformanceEvaluationMethodList.fulfilled.type]: (
			state,
			action
		) => {
			if (action.payload && action.payload.length > 0) {
				state.allPerformanceEvaluationMethodList = action.payload;
			}
		},
		[fetchAllPerformanceEvaluationMethodList.rejected.type]: (
			_state,
			_action
		) => {
			Shared.showSnackbarMessage(
				"Error while fetching performance evaluation method list, please try again!"
			);
		},
		[fetchAllPerformanceEvaluationTypeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allPerformanceEvaluationTypeList = action.payload;
			}
		},
		[fetchAllPerformanceEvaluationTypeList.rejected.type]: (
			_state,
			_action
		) => {
			Shared.showSnackbarMessage(
				"Error while fetching performance evaluation type list, please try again!"
			);
		},
		[fetchAllRatingScaleList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allRatingScaleList = action.payload;
			}
		},
		[fetchAllRatingScaleList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching rating scale list, please try again!"
			);
		},
		[fetchAllApprovedActiveScopeOfWorkList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.allApprovedScopeOfWorkList = action.payload;
			}
		},
		[fetchAllApprovedActiveScopeOfWorkList.rejected.type]: (
			_state,
			_action
		) => {
			Shared.showSnackbarMessage(
				"Error while fetching scope of work list, please try again!"
			);
		},
	},
});

export const {
	setSelectedDashboardFilter,
	setSelectedContractDashboardFilter,
} = masterPageStateSlice.actions;

export default masterPageStateSlice.reducer;
